import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Progress } from '../ui/progress';
import type {
  DashboardItemStateCount,
  DashboardOrderStatusCount,
  DashboardProjectStatusCount,
} from '../../types/dashboard';
import type { OrderStatus } from '../../types/orders';
import { formatProjectStatus, type ProjectStatus } from '../../types/projects';
import type { RoomItemState } from '../../types/subprojects';

type DashboardPipelineCardProps = {
  itemsByState: DashboardItemStateCount[];
  ordersByStatus: DashboardOrderStatusCount[];
  projectsByStatus: DashboardProjectStatusCount[];
};

type BadgeVariant = 'planning' | 'inProgress' | 'quoted' | 'finished' | 'secondary' | 'destructive';

type PipelineRow = {
  key: string;
  label: string;
  count: number;
  variant: BadgeVariant;
};

const PROJECT_STATUS_BADGES: Record<ProjectStatus, BadgeVariant> = {
  planificacion: 'planning',
  en_progreso: 'inProgress',
  presupuestado: 'quoted',
  finalizado: 'finished',
};

const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  borrador: 'Borrador',
  pedido: 'Pedido',
  confirmado: 'Confirmado',
  enviado: 'Enviado',
  recibido: 'Recibido',
  cancelado: 'Cancelado',
};

const ORDER_STATUS_BADGES: Record<OrderStatus, BadgeVariant> = {
  borrador: 'secondary',
  pedido: 'quoted',
  confirmado: 'inProgress',
  enviado: 'inProgress',
  recibido: 'finished',
  cancelado: 'destructive',
};

const ITEM_STATE_LABELS: Record<RoomItemState, string> = {
  pendiente: 'Pendiente',
  pedido: 'Pedido',
  recibido: 'Recibido',
  instalado: 'Instalado',
};

const ITEM_STATE_BADGES: Record<RoomItemState, BadgeVariant> = {
  pendiente: 'secondary',
  pedido: 'inProgress',
  recibido: 'quoted',
  instalado: 'finished',
};

function PipelineSection({ rows, title }: { rows: PipelineRow[]; title: string }) {
  const total = rows.reduce((sum, row) => sum + row.count, 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-900">{title}</h3>
        <span className="text-sm text-gray-500">{total} total</span>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">Sin datos disponibles.</p>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => {
            const percentage = total > 0 ? Math.round((row.count / total) * 100) : 0;
            return (
              <div key={row.key} className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Badge variant={row.variant}>{row.label}</Badge>
                    <span className="text-sm text-gray-500">{row.count}</span>
                  </div>
                  <span className="text-xs text-gray-500">{percentage}%</span>
                </div>
                <Progress
                  value={percentage}
                  className="h-2 bg-blue-100"
                  indicatorClassName="bg-blue-600"
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function DashboardPipelineCard({
  itemsByState,
  ordersByStatus,
  projectsByStatus,
}: DashboardPipelineCardProps) {
  const projectRows: PipelineRow[] = projectsByStatus.map((item) => ({
    key: item.status,
    label: formatProjectStatus(item.status),
    count: item.count,
    variant: PROJECT_STATUS_BADGES[item.status],
  }));
  const orderRows: PipelineRow[] = ordersByStatus.map((item) => ({
    key: item.status,
    label: ORDER_STATUS_LABELS[item.status],
    count: item.count,
    variant: ORDER_STATUS_BADGES[item.status],
  }));
  const itemRows: PipelineRow[] = itemsByState.map((item) => ({
    key: item.state,
    label: ITEM_STATE_LABELS[item.state],
    count: item.count,
    variant: ITEM_STATE_BADGES[item.state],
  }));

  return (
    <Card className="shadow-sm">
      <CardHeader>
        <CardTitle>Resumen operativo</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <PipelineSection title="Proyectos por estado" rows={projectRows} />
        <div className="border-t border-gray-100 pt-6">
          <PipelineSection title="Pedidos por estado" rows={orderRows} />
        </div>
        <div className="border-t border-gray-100 pt-6">
          <PipelineSection title="Items por estado" rows={itemRows} />
        </div>
      </CardContent>
    </Card>
  );
}
